import React from 'react';
import { Divider, Field, Input, Select } from '@fluentui/react-components';
import { FormProvider, useForm } from 'react-hook-form';
import { useAppActions, useAppState } from '../../context';
import { getValidationState } from './components/formValidation';
import { PageReferenceActivityGroup } from '@gkn-ili/views/dist/OrderDocumentation';
import { AppEditDialogActions } from './components/dialogActions';
import {
  AppEditContentFormFields,
  AppEditContentGetDefaultValues,
  AppEditContentGetSaveData,
} from './components/editContent';
import { View3DCameraState } from '../../context/context3D';
import { useLargeModalStyles } from './components/commonStyles';
import {
  AppDialogBody,
  AppDialogContent,
  AppDialogFooter,
  AppDialogHeader,
  AppDialogSurface,
  DialogOptions,
} from './components/baseComponents';
import AppView3DConfig from './components/view3DConfig';
import { findKeyBefore } from '../../utils/arrayHelpers';
import AppModelTreeGrid, { defaultFieldGenerators } from './components/modelTreeGrid';

type FormState = { name: string; description: string; previousActivityGroup: string } & AppEditContentFormFields;

export default function AppEditActivityGroupModal(props: {
  activityGroupReference: PageReferenceActivityGroup;
  cameraView?: View3DCameraState;
  dialogOptions: DialogOptions;
}): JSX.Element {
  const styles = useLargeModalStyles();

  const appState = useAppState();
  const appActions = useAppActions();
  const step =
    appState.orderView.routing[props.activityGroupReference.routingItemKey].steps[props.activityGroupReference.stepKey];
  const activityGroup = step.activityGroups[props.activityGroupReference.activityGroupKey];

  const formHandlers = useForm<FormState>({
    defaultValues: {
      name: activityGroup.name,
      description: activityGroup.description,
      previousActivityGroup: findKeyBefore(
        Object.keys(step.activityGroups),
        props.activityGroupReference.activityGroupKey,
      ),
      ...AppEditContentGetDefaultValues(activityGroup),
    },
  });
  const valueName = formHandlers.watch('name');
  const valueDescription = formHandlers.watch('description');
  const valueContentType = formHandlers.watch('contentType');
  const modifiers = formHandlers.watch('model.model.configuration.modifiers');

  function onHideShow(reference: string[], visible: boolean) {
    const allData = formHandlers.getValues();
    const actualModifiers = allData.model.model.configuration.modifiers;
    formHandlers.setValue('model.model.configuration.modifiers', [...actualModifiers, { reference, visible }]);
  }
  const onSubmit = formHandlers.handleSubmit(async (data) => {
    const content = AppEditContentGetSaveData(data, activityGroup, props.cameraView);
    await appActions.editOrder.editActivityGroup(
      props.activityGroupReference,
      data.name,
      data.description,
      content,
      data.previousActivityGroup,
    );
    props.dialogOptions.onCloseDialog();
  });

  const modelContent = (
    <>
      <div className={styles.groupModelTree}>
        <Divider>3D Data</Divider>
        <AppModelTreeGrid
          fieldGenerators={[
            defaultFieldGenerators.hideShow(
              modifiers.map((rule) => ({ ...rule, inheritanceLevel: 0 })),
              onHideShow,
            ),
          ]}
        />
      </div>
      <div className={styles.groupViewConfig}>
        <AppView3DConfig />
      </div>
    </>
  );

  return (
    <AppDialogSurface dialogOptions={props.dialogOptions} className={styles.root}>
      <FormProvider {...formHandlers}>
        <form>
          <AppDialogBody dialogOptions={props.dialogOptions}>
            <AppDialogHeader dialogOptions={props.dialogOptions}>Edit Activity Group</AppDialogHeader>
            <AppDialogContent dialogOptions={props.dialogOptions} className={styles.twoColumnContainer}>
              <Divider className={styles.groupBasicHeader}>Basic</Divider>

              <div className={styles.groupBasicLH}>
                <Field
                  orientation="horizontal"
                  label="Activity group name"
                  required
                  {...getValidationState('name', formHandlers.formState.errors)}
                >
                  <Input
                    {...formHandlers.register('name', { required: true })}
                    name="name"
                    value={valueName}
                    className={styles.field}
                  />
                </Field>
                <Field
                  orientation="horizontal"
                  label="Activity group description"
                  {...getValidationState('description', formHandlers.formState.errors)}
                >
                  <Input {...formHandlers.register('description')} value={valueDescription} className={styles.field} />
                </Field>
              </div>
              <div className={styles.groupBasicRH}>
                <Field
                  orientation="horizontal"
                  label="Content type"
                  required
                  {...getValidationState('contentType', formHandlers.formState.errors)}
                >
                  <Select {...formHandlers.register('contentType')}>
                    <option value="text">Text</option>
                    <option value="model">3D Model</option>
                  </Select>
                </Field>
                <Field
                  orientation="horizontal"
                  label="Comes after activityGroup"
                  required
                  {...getValidationState('previousActivityGroup', formHandlers.formState.errors)}
                >
                  <Select {...formHandlers.register('previousActivityGroup', { required: true })}>
                    <option value="none">As first</option>
                    {Object.entries(step.activityGroups)
                      .filter(([key]) => key !== props.activityGroupReference.activityGroupKey)
                      .map(([key, value]) => (
                        <option key={key} value={key}>
                          {value.name}
                        </option>
                      ))}
                  </Select>
                </Field>
              </div>
              {valueContentType === 'model' ? modelContent : <span>Not implemented</span>}
            </AppDialogContent>
            <AppDialogFooter dialogOptions={props.dialogOptions}>
              <AppEditDialogActions
                onCancel={props.dialogOptions.onCloseDialog}
                submitDisabled={formHandlers.formState.isSubmitting}
                onSubmit={onSubmit}
              />
            </AppDialogFooter>
          </AppDialogBody>
        </form>
      </FormProvider>
    </AppDialogSurface>
  );
}
